import { Grid, Text } from '@simplywallst/ui-core';

import { isCurrencyInfo } from '@/utilities/isCurrencyInfo';

import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { ContainerWithPlaceHolder } from './styled';
import ReportSubSection from '@/components/ReportSubSection';
import loadable from '@loadable/component';
import { useCompanyTimeSeries } from '@/pages/CompanyReport/components/Past/hooks/useCompanyTimeSeries';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { usePastTranslation } from '@/pages/CompanyReport/components/Past/hooks/usePastTranslation';
import { Sankey } from './components/Sankey';
import type { HeadingElements } from '@simplywallst/ui-core/lib/src/components/Heading/types';

const HistoricalFinanceTable = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Past/components/ExtendedIncomeStatement/components/HistoricalFinanceTable'
    ),
  {
    resolveComponent: (c) => c.HistoricalFinanceTable,
  }
);

interface Props {
  companyName: string;
  titleElement: HeadingElements;
}

export function ExtendedIncomeStatement({ companyName, titleElement }: Props) {
  const canonicalUrl = useCompanyCanonicalUrl();

  const { data: rawData, status } = useCompanyTimeSeries({
    canonicalUrl,
    period: 'MAX',
  });

  const t = usePastTranslation();

  const currencyInfo = rawData?.data.currencyInfo;

  return (
    <>
      <ReportSubSection
        titleElement={titleElement}
        title={
          <Grid templateColumns="auto auto" alignItems="center" gap="8px">
            <div>{t('revenue.chart.title')}</div>
          </Grid>
        }
        description={t('revenue.chart.description', { company: companyName })}
        trackingProps={{ subject: 'chart' }}
        bodyRender={() => (
          <ChartWrapper
            id="extended-income-statement"
            chart={
              status === 'error' ? (
                <ContainerWithPlaceHolder placeItems="center">
                  <Text typography="smallStrong">
                    {t('revenue.chart.insufficientData')}
                  </Text>
                </ContainerWithPlaceHolder>
              ) : (
                <Sankey
                  gqlData={rawData}
                  currencyISO={
                    isCurrencyInfo(currencyInfo)
                      ? currencyInfo.reporting_currency_iso
                      : 'USD'
                  }
                  labels={{
                    node: (name) => t(`revenue.chart.node.${name}`),
                    insufficientData: t('revenue.chart.insufficientData'),
                  }}
                />
              )
            }
            preRenderHeight={560}
            preRenderMinHeightMdDown={320}
            actionsMarginTop={16}
            feedbackLabel={t('revenue.chart.title')}
            isLoading={status === 'pending'}
            hideTableScrollbars
            table={<HistoricalFinanceTable rawData={rawData} />}
          />
        )}
      />
      <hr />
    </>
  );
}
